import ReceiptIcon from '@mui/icons-material/Receipt.js';
import { Stack } from '@mui/material';
import Divider from '@mui/material/Divider';
import React, { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { CopyBarcode, CopyQRCode, ActionButton , BrowserOnly } from '@woovi/ui';

import type { PaymentActiveBoleto_charge$key } from './__generated__/PaymentActiveBoleto_charge.graphql.ts';
import PaymentActiveBarcode from './PaymentActiveBarcode.tsx';
import PaymentActiveBoletoDelinquencyWarning from './PaymentActiveBoletoDelinquencyWarning.tsx';
import PaymentActiveOpenBankInfoBoleto from './PaymentActiveOpenBankInfoBoleto.tsx';
import PaymentMethodBoletoValue from './PaymentMethodBoletoValue.tsx';
import { useChargePaymentMethod } from '../../../charge/useChargePaymentMethod.tsx';
import ChargePrintPDFButton from '../../active/paymentLinkActive/pdf/ChargePrintPDF.tsx';
import PaymentLinkAdditionalInfo from '../../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../../paymentLink/PaymentLinkComment.tsx';
import PaymentLinkShareButton from '../../paymentLink/PaymentLinkShareButton.tsx';
import PaymentMethodPaymentDeadline from '../../paymentMethod/PaymentMethodPaymentDeadline.tsx';
import PaymentMethodTransactionDetails from '../../paymentMethod/PaymentMethodTransactionDetails.tsx';
import PaymentLinkActiveEarnDiscount from '../paymentLinkActive/PaymentLinkActiveEarnDiscount.tsx';
import PaymentLinkActiveQrCode from '../paymentLinkActive/PaymentLinkActiveQrCode.tsx';

type PaymentActiveBoletoProps = {
  charge: PaymentActiveBoleto_charge$key;
};

const PaymentActiveBoleto = (props: PaymentActiveBoletoProps): ReactNode => {
  const [barcodeShow, setBarcodeShow] = useState(false);
  const [copyBarcodeError, setCopyBarcodeError] = useState(false);
  const [qrCodeShow, setQrCodeShow] = useState(false);
  const [copyQRCodeError, setCopyQRCodeError] = useState(false);
  const { t } = useTranslation();

  const charge = useFragment<PaymentActiveBoleto_charge$key>(
    graphql`
      fragment PaymentActiveBoleto_charge on Charge {
        ...PaymentLinkComment_charge
        ...PaymentLinkActiveEarnDiscount_charge
        ...PaymentLinkAdditionalInfo_charge
        ...PaymentLinkShareButton_charge
        ...PaymentActiveBarcode_charge
        ...useChargePaymentMethod_charge
        ...PaymentMethodPaymentDeadline_charge
        ...PaymentMethodTransactionDetails_charge
        ...PaymentMethodBoletoValue_charge
        ...PaymentActiveBoletoDelinquencyWarning_charge
        ...PaymentLinkActiveQrCode_charge
        ...ChargePrintPDF_charge
      }
    `,
    props.charge,
  );

  const { boleto, pix } = useChargePaymentMethod(charge);

  return (
    <Stack gap={2}>
      <PaymentMethodBoletoValue charge={charge} />
      <PaymentLinkComment charge={charge} />
      <Stack gap={1}>
        <PaymentLinkActiveEarnDiscount charge={charge} />
        <Stack sx={{ gap: 1, alignItems: 'center' }}>
          <Stack sx={{ minWidth: 300 }}>
            <PaymentLinkActiveQrCode
              charge={charge}
              qrCodeShow={qrCodeShow}
              setQrCodeShow={setQrCodeShow}
              copyQRCodeError={copyQRCodeError}
              alwaysShowQrCode
            />
          </Stack>
          {pix.brCode && (
            <>
              <CopyQRCode
                value={pix.brCode}
                onError={(error) => setCopyQRCodeError(error)}
              />
              <Divider />
            </>
          )}
          <PaymentActiveBarcode
            charge={charge}
            barcodeShow={barcodeShow}
            setBarcodeShow={setBarcodeShow}
            copyBarcodeError={copyBarcodeError}
          />
          <PaymentActiveOpenBankInfoBoleto />
        </Stack>
        <CopyBarcode
          value={boleto.boletoDigitable as string}
          onError={(error) => setCopyBarcodeError(error)}
        />
        <PaymentActiveBoletoDelinquencyWarning charge={charge} />
        <PaymentLinkShareButton charge={charge} />
        <BrowserOnly>
          <ChargePrintPDFButton
            charge={charge}
            trigger={({ disabled, onPrint }) => (
              <ActionButton
                disabled={disabled}
                onClick={onPrint}
                variant='outlined'
                color='tertiary'
                size='large'
                sx={(theme) => ({
                  '&:hover': {
                    color: theme.palette.tertiary.main,
                  },
                  '&:focus': {
                    color: theme.palette.tertiary.main,
                    backgroundColor: theme.palette.common.white,
                  },
                })}
                startIcon={
                  <ReceiptIcon color={disabled ? 'disabled' : 'tertiary'} />
                }
              >
                {t('DOWNLOAD PIX INVOICE')}
              </ActionButton>
            )}
          />
        </BrowserOnly>
      </Stack>
      <Stack sx={{ gap: 1 }}>
        <PaymentMethodPaymentDeadline charge={charge} />
        <PaymentMethodTransactionDetails charge={charge} />
        <PaymentLinkAdditionalInfo charge={charge} />
      </Stack>
    </Stack>
  );
};

export default PaymentActiveBoleto;
